import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CarFront,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  Moon,
  Phone,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Users,
  XCircle,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import AdminSidebar from "../components/AdminSidebar";
import useAdminTheme from "../hooks/useAdminTheme";
import { safeTrim, sanitizeInput } from "../utils/security";

function AdminPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const { isDark: isDarkTheme, toggleTheme } = useAdminTheme();

  const fetchBookings = useCallback(async () => {
    // Security assumption: admin booking reads are allowed only by Supabase RLS
    // policies for the authenticated admin user verified by ProtectedAdminRoute.
    const { data, error } = await supabase
      .from("bookings")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
    } else {
      setBookings(data);
    }
  }, []);

  useEffect(() => {
  if (selectedBooking) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }

  return () => {
    document.body.style.overflow = "auto";
  };
}, [selectedBooking]);

  useEffect(() => {
  fetchBookings();
}, [fetchBookings]);

  const updateBookingStatus = async (id, status) => {
  const allowedStatuses = ["new", "confirmed", "completed", "cancelled"];
  const nextStatus = sanitizeInput(status);

  if (!allowedStatuses.includes(nextStatus)) {
    return;
  }

  // Security assumption: status updates depend on authenticated admin RLS.
  const { error } = await supabase
    .from("bookings")
    .update({ status: nextStatus })
    .eq("id", id);

  if (error) {
    console.error(error);
  } else {
    fetchBookings();
  }
};

const handleLogout = async () => {
  await supabase.auth.signOut();
  navigate("/admin-login");
};

const filteredBookings = bookings.filter((booking) => {
  const sanitizedSearchTerm = sanitizeInput(searchTerm).toLowerCase();
  const sanitizedDateFilter = safeTrim(dateFilter);
  const matchesSearch =
    booking.pickup?.toLowerCase().includes(sanitizedSearchTerm) ||
    booking.destination?.toLowerCase().includes(sanitizedSearchTerm) ||
    booking.phone?.toLowerCase().includes(sanitizedSearchTerm);

 const matchesStatus =
  statusFilter === "all" || booking.status === statusFilter;

const bookingCreatedDate = booking.created_at
  ? booking.created_at.split("T")[0]
  : "";

const matchesDate =
  !sanitizedDateFilter || bookingCreatedDate === sanitizedDateFilter;

return matchesSearch && matchesStatus && matchesDate;
});

const totalBookings = bookings.length;
const newBookings = bookings.filter((b) => b.status === "new").length;
const confirmedBookings = bookings.filter((b) => b.status === "confirmed").length;
const completedBookings = bookings.filter((b) => b.status === "completed").length;
const cancelledBookings = bookings.filter((b) => b.status === "cancelled").length;

const formatDate = (dateString) => {
  if (!dateString) return "-";

  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  return new Date(dateString).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isASAPBooking = (booking) => booking?.isASAP === true;

const isAirportLocation = (location) => {
  if (!location) return false;

  const normalizedLocation = location.toLowerCase();

  return (
    normalizedLocation.includes("airport") ||
    normalizedLocation.includes("larnaca international airport") ||
    normalizedLocation.includes("paphos international airport") ||
    /\blca\b/.test(normalizedLocation) ||
    /\bpfo\b/.test(normalizedLocation)
  );
};

const shortenLocation = (location) => {
  if (!location) return "-";

  const lowerLocation = location.toLowerCase();

  if (lowerLocation.includes("larnaca") && lowerLocation.includes("airport")) {
    return "Larnaca Airport";
  }

  if (lowerLocation.includes("paphos") && lowerLocation.includes("airport")) {
    return "Paphos Airport";
  }

  return location
    .split(",")[0]
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getPhoneHref = (phone) => {
  if (!phone) return "";

  return `tel:${phone.replace(/[^\d+]/g, "")}`;
};

const getStatusClassName = (status, isDarkMode = false) => {
  const statusClasses = isDarkMode ? {
    new: "border-yellow-400/40 bg-yellow-400/15 text-yellow-200",
    confirmed: "border-sky-400/40 bg-sky-400/10 text-sky-200",
    completed: "border-emerald-400/40 bg-emerald-400/10 text-emerald-200",
    cancelled: "border-red-400/40 bg-red-400/10 text-red-200",
  } : {
    new: "border-yellow-300 bg-yellow-50 text-yellow-800",
    confirmed: "border-sky-200 bg-sky-50 text-sky-800",
    completed: "border-emerald-200 bg-emerald-50 text-emerald-800",
    cancelled: "border-red-200 bg-red-50 text-red-800",
  };

  return statusClasses[status] || (
    isDarkMode
      ? "border-white/10 bg-neutral-900 text-white"
      : "border-slate-200 bg-slate-50 text-slate-700"
  );
};

const getStatusDotClassName = (status) => {
  const dotClasses = {
    new: "bg-yellow-400",
    confirmed: "bg-sky-400",
    completed: "bg-emerald-400",
    cancelled: "bg-red-400",
  };

  return dotClasses[status] || "bg-neutral-400";
};


const calculateEstimatedPrice = (pickup, destination, luggage) => {
  const route = `${pickup} ${destination}`.toLowerCase();

  let price = 15;

  if (route.includes("larnaca") && route.includes("airport")) {
    price = 70;
  } else if (route.includes("paphos") && route.includes("airport")) {
    price = 75;
  } else if (route.includes("nicosia")) {
    price = 90;
  } else if (route.includes("ayia napa")) {
    price = 130;
  } else if (route.includes("protaras")) {
    price = 140;
  }

  return price;
};

const updateFinalPrice = async (id, finalPrice) => {
  const sanitizedFinalPrice = sanitizeInput(finalPrice);

  // Security assumption: price updates depend on authenticated admin RLS.
  const { error } = await supabase
    .from("bookings")
    .update({ final_price: sanitizedFinalPrice || null })
    .eq("id", id);

  if (error) {
    console.error(error);
  } else {
    fetchBookings();
  }
};

const totalRevenue = bookings.reduce((sum, booking) => {
  return sum + parseFloat(booking.final_price || 0);
}, 0);

const summaryCards = [
  {
    label: "Total bookings",
    value: totalBookings,
    icon: CarFront,
    helper: `${filteredBookings.length} showing`,
  },
  {
    label: "New",
    value: newBookings,
    icon: Clock3,
    helper: "Awaiting review",
  },
  {
    label: "Confirmed",
    value: confirmedBookings,
    icon: ShieldCheck,
    helper: "Ready to dispatch",
  },
  {
    label: "Completed",
    value: completedBookings,
    icon: CheckCircle2,
    helper: "Finished rides",
  },
  {
    label: "Cancelled",
    value: cancelledBookings,
    icon: XCircle,
    helper: "Removed trips",
  },
  {
    label: "Revenue",
    value: `€${totalRevenue.toLocaleString("en-GB")}`,
    icon: CircleDollarSign,
    helper: "Final prices",
  },
];

const saveBookingChanges = async () => {
  const allowedStatuses = ["new", "confirmed", "completed", "cancelled"];
  const nextStatus = sanitizeInput(editingBooking.status);

  if (!allowedStatuses.includes(nextStatus)) {
    return;
  }

  // Security assumption: booking edits depend on authenticated admin RLS.
  const { error } = await supabase
    .from("bookings")
    .update({
      phone: sanitizeInput(editingBooking.phone),
      status: nextStatus,
      final_price: sanitizeInput(editingBooking.final_price),
    })
    .eq("id", editingBooking.id);

  if (!error) {
    fetchBookings();
    setSelectedBooking(null);
    setEditingBooking(null);
  }
};

const theme = {
  page: isDarkTheme ? "bg-[#080808] text-white" : "bg-slate-50 text-slate-950",
  card: isDarkTheme
    ? "border-white/10 bg-neutral-950 shadow-2xl shadow-black/30"
    : "border-slate-200 bg-white shadow-sm",
  softCard: isDarkTheme
    ? "border-white/10 bg-black/30"
    : "border-slate-100 bg-slate-50",
  mutedText: isDarkTheme ? "text-neutral-400" : "text-slate-500",
  faintText: isDarkTheme ? "text-neutral-500" : "text-slate-400",
  strongText: isDarkTheme ? "text-white" : "text-slate-950",
  bodyText: isDarkTheme ? "text-neutral-300" : "text-slate-700",
  divider: isDarkTheme ? "border-white/10" : "border-slate-200",
  input: isDarkTheme
    ? "border-white/10 bg-black/40 text-white placeholder:text-neutral-600"
    : "border-slate-200 bg-slate-50 text-slate-800 placeholder:text-slate-400 focus:bg-white",
  tableHead: isDarkTheme
    ? "bg-white/[0.04] text-neutral-500"
    : "bg-slate-50 text-slate-400",
  tableRow: isDarkTheme
    ? "bg-neutral-950 hover:bg-white/[0.04]"
    : "bg-white hover:bg-slate-50",
  tableDivide: isDarkTheme ? "divide-white/10" : "divide-slate-100",
  yellowBadge: isDarkTheme
    ? "border border-yellow-400/20 bg-yellow-400/10 text-yellow-300"
    : "bg-yellow-100 text-yellow-800",
};

const clearFilters = () => {
  setDateFilter("");
  setSearchTerm("");
  setStatusFilter("all");
};
  

  return (
    <div className={`min-h-screen ${theme.page}`}>
      <AdminSidebar />

      <main
        className="min-h-screen px-4 py-5 transition-all duration-300 sm:px-6 lg:px-8"
        style={{ marginLeft: "var(--admin-sidebar-width, 16rem)" }}
      >
        <div className="mx-auto max-w-[1500px]">
          <header className={`mb-6 rounded-2xl border p-5 sm:p-6 ${theme.card}`}>
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className={`mb-3 flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ${theme.yellowBadge}`}>
                  <CalendarClock size={14} />
                  Live operations
                </div>
                <h1 className={`text-3xl font-black tracking-tight sm:text-4xl ${theme.strongText}`}>
                  Taxi Admin
                </h1>
                <p className={`mt-2 max-w-2xl text-sm ${theme.mutedText}`}>
                  Manage bookings, prices, and trip status.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className={`rounded-2xl border px-4 py-3 ${theme.softCard}`}>
                  <p className={`text-xs font-bold uppercase tracking-wide ${theme.faintText}`}>
                    Bookings
                  </p>
                  <p className={`text-xl font-black ${isDarkTheme ? "text-yellow-300" : "text-slate-950"}`}>
                    {filteredBookings.length} visible
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-black transition ${
                    isDarkTheme
                      ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                      : "border-slate-200 bg-white text-slate-800 hover:border-yellow-300 hover:bg-yellow-50"
                  }`}
                >
                  {isDarkTheme ? <Sun size={17} /> : <Moon size={17} />}
                  {isDarkTheme ? "Light" : "Dark"}
                </button>
                <button
                  onClick={handleLogout}
                  className={`rounded-2xl border px-5 py-3 text-sm font-black transition ${
                    isDarkTheme
                      ? "border-red-400/30 bg-red-500/10 text-red-100 hover:bg-red-500 hover:text-white"
                      : "border-red-100 bg-red-50 text-red-700 hover:border-red-200 hover:bg-red-100"
                  }`}
                >
                  Logout
                </button>
              </div>
            </div>
          </header>

          <section className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            {summaryCards.map(({ label, value, icon: Icon, helper }) => (
              <div
                key={label}
                className={`rounded-2xl border p-5 ${theme.card}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className={`text-sm font-semibold ${theme.mutedText}`}>{label}</p>
                  <span className={`rounded-2xl p-2 ${
                    isDarkTheme
                      ? "border border-yellow-400/20 bg-yellow-400/10 text-yellow-300"
                      : "bg-yellow-100 text-yellow-700"
                  }`}>
                    <Icon size={18} />
                  </span>
                </div>
                <p className={`mt-5 text-3xl font-black tracking-tight ${theme.strongText}`}>
                  {value}
                </p>
                <p className={`mt-2 text-xs font-medium ${theme.faintText}`}>
                  {helper}
                </p>
              </div>
            ))}
          </section>

          <section className={`mb-6 rounded-2xl border p-4 sm:p-5 ${theme.card}`}>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className={`flex items-center gap-2 text-sm font-black ${theme.bodyText}`}>
                <SlidersHorizontal size={18} className={isDarkTheme ? "text-yellow-300" : "text-yellow-700"} />
                Filters
              </div>
              <button
                type="button"
                onClick={clearFilters}
                className={`inline-flex w-fit items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  isDarkTheme
                    ? "border-white/10 bg-white/5 text-neutral-300 hover:bg-white/10 hover:text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-yellow-300 hover:bg-yellow-50 hover:text-slate-950"
                }`}
              >
                <RotateCcw size={15} />
                Clear filters
              </button>
            </div>
            <div className="grid gap-3 lg:grid-cols-[220px_1fr_220px]">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className={`h-12 rounded-2xl border px-4 text-sm font-semibold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${theme.input}`}
              />

              <label className="relative block">
                <Search
                  size={18}
                  className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${theme.faintText}`}
                />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search pickup, destination, or phone..."
                  className={`h-12 w-full rounded-2xl border py-3 pl-11 pr-4 text-sm font-semibold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${theme.input}`}
                />
              </label>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`h-12 rounded-2xl border px-4 text-sm font-bold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${theme.input}`}
              >
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </section>

          <section className={`rounded-2xl border ${theme.card}`}>
            <div className={`flex flex-col gap-2 border-b px-5 py-5 sm:flex-row sm:items-end sm:justify-between ${theme.divider}`}>
              <div>
                <h2 className={`text-xl font-black ${theme.strongText}`}>Bookings</h2>
                <p className={`mt-1 text-sm ${theme.mutedText}`}>
                  Review routes, prices, and trip status
                </p>
              </div>
              <p className={`text-sm font-bold ${isDarkTheme ? "text-yellow-300" : "text-blue-700"}`}>
                {filteredBookings.length} of {totalBookings} records
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1320px] table-fixed border-collapse">
                <colgroup>
                  <col className="w-[160px]" />
                  <col className="w-[150px]" />
                  <col className="w-[340px]" />
                  <col className="w-[180px]" />
                  <col className="w-[150px]" />
                  <col className="w-[120px]" />
                  <col className="w-[140px]" />
                  <col className="w-[180px]" />
                </colgroup>
                <thead className={`text-xs uppercase tracking-[0.16em] ${theme.tableHead}`}>
                  <tr>
                    <th className="px-5 py-4 text-left font-bold">Ordered</th>
                    <th className="px-5 py-4 text-left font-bold">Pickup</th>
                    <th className="px-5 py-4 text-left font-bold">Route</th>
                    <th className="px-5 py-4 text-left font-bold">Phone</th>
                    <th className="px-5 py-4 text-left font-bold">Details</th>
                    <th className="px-5 py-4 text-right font-bold">Estimated</th>
                    <th className="px-5 py-4 text-center font-bold">Final Price</th>
                    <th className="px-5 py-4 text-left font-bold">Status</th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${theme.tableDivide}`}>
                  {filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      onClick={() => {
                        setSelectedBooking(booking);
                        setEditingBooking(booking);
                      }}
                      className={`cursor-pointer transition ${theme.tableRow}`}
                    >
                      <td className="px-5 py-5 align-top">
                        <p className={`font-semibold ${theme.strongText}`}>
                          {formatDateTime(booking.created_at)}
                        </p>
                        <p className={`mt-1 text-xs ${theme.faintText}`}>
                          #{booking.id?.slice(0, 8)}
                        </p>
                      </td>
                      <td className="px-5 py-5 align-top">
                        <div className="flex flex-col gap-2">
                          <span className={`font-semibold ${theme.strongText}`}>
                            {formatDate(booking.date)}
                          </span>
                          <span className={`text-sm ${theme.mutedText}`}>
                            {booking.time || "-"}
                          </span>
                          {isASAPBooking(booking) && (
                            <span className="w-fit rounded-full bg-yellow-400 px-2.5 py-1 text-xs font-black uppercase text-black">
                              ASAP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-5 align-top">
                        <div className="space-y-3 text-sm leading-relaxed">
                          <div className="flex gap-3">
                            <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-yellow-400" />
                            <div className="min-w-0">
                              <p className={`font-semibold ${theme.strongText}`}>
                                {shortenLocation(booking.pickup)}
                              </p>
                              {isAirportLocation(booking.pickup) && (
                                <p className={`mt-1 w-fit rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${theme.yellowBadge}`}>
                                  Airport
                                </p>
                              )}
                            </div>
                          </div>
                          <div className={`ml-1 h-5 w-px ${isDarkTheme ? "bg-white/15" : "bg-slate-200"}`} />
                          <div className="flex gap-3">
                            <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-neutral-500" />
                            <div className="min-w-0">
                              <p className={`font-semibold ${theme.bodyText}`}>
                                {shortenLocation(booking.destination)}
                              </p>
                              {isAirportLocation(booking.destination) && (
                                <p className={`mt-1 w-fit rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${theme.yellowBadge}`}>
                                  Airport
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-5 align-top">
                        {booking.phone ? (
                          <a
                            href={getPhoneHref(booking.phone)}
                            onClick={(e) => e.stopPropagation()}
                            className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-sm font-bold transition ${
                              isDarkTheme
                                ? "border-yellow-400/20 bg-yellow-400/10 text-yellow-200 hover:border-yellow-300 hover:bg-yellow-400/20"
                                : "border-yellow-200 bg-yellow-50 text-yellow-800 hover:border-yellow-300 hover:bg-yellow-100"
                            }`}
                          >
                            <Phone size={14} className="shrink-0" />
                            <span className="truncate">{booking.phone}</span>
                          </a>
                        ) : (
                          <span className={theme.faintText}>-</span>
                        )}
                      </td>
                      <td className="px-5 py-5 align-top">
                        <div className={`space-y-2 text-sm ${theme.bodyText}`}>
                          <p className="flex items-center gap-2">
                            <Users size={15} className={theme.faintText} />
                            {booking.passengers || 0} passengers
                          </p>
                          <p>{booking.luggage || 0} luggage</p>
                          {booking.flight_number && (
                            <p className={`truncate text-xs ${theme.faintText}`}>
                              Flight {booking.flight_number}
                            </p>
                          )}
                        </div>
                      </td>

                      <td className={`px-5 py-5 text-right align-top text-lg font-black ${isDarkTheme ? "text-yellow-300" : "text-yellow-700"}`}>
                        €{calculateEstimatedPrice(
                          booking.pickup,
                          booking.destination,
                          booking.luggage
                        )}
                      </td>

                      <td className="px-5 py-5 text-center align-top">
                        <input
                          type="number"
                          onClick={(e) => e.stopPropagation()}
                          defaultValue={
                            booking.final_price ||
                            calculateEstimatedPrice(
                              booking.pickup,
                              booking.destination,
                              booking.luggage
                            )
                          }
                          onBlur={(e) =>
                            updateFinalPrice(booking.id, e.target.value)
                          }
                          className={`h-11 w-28 rounded-2xl border px-3 text-center font-bold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${theme.input}`}
                        />
                      </td>

                      <td className="px-5 py-5 align-top">
                        <div className="relative">
                          <span
                            className={`pointer-events-none absolute left-3 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${getStatusDotClassName(
                              booking.status
                            )}`}
                          />
                          <select
                            value={booking.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              updateBookingStatus(booking.id, e.target.value)
                            }
                            className={`h-11 w-full rounded-2xl border py-2 pl-8 pr-3 text-sm font-bold capitalize outline-none transition focus:ring-2 focus:ring-yellow-400/20 ${getStatusClassName(
                              booking.status,
                              isDarkTheme
                            )}`}
                          >
                            <option value="new">New</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredBookings.length === 0 && (
                <div className={`border-t px-6 py-14 text-center ${theme.divider}`}>
                  <p className={`text-lg font-bold ${theme.strongText}`}>No bookings found</p>
                  <p className={`mt-2 text-sm ${theme.mutedText}`}>
                    Adjust the filters to see more results.
                  </p>
                </div>
              )}
            </div>
          </section>

          {selectedBooking && (
            
  <div className={`fixed inset-0 z-[100] flex items-center justify-center overflow-hidden px-6 backdrop-blur-sm ${
    isDarkTheme ? "bg-black/90" : "bg-slate-950/55"
  }`}>
          <div className={`mx-auto flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${theme.card}`}>

  <div className={`flex items-center justify-between border-b p-8 ${theme.divider}`}>
    <div>
      <h2 className={`text-3xl font-black ${isDarkTheme ? "text-yellow-400" : "text-slate-950"}`}>
        Booking Details
      </h2>

      <p className={`text-sm ${theme.faintText}`}>
        Booking #{selectedBooking.id?.slice(0, 8)}
      </p>
    </div>

    <button
      onClick={() => setSelectedBooking(null)}
      className={`rounded-xl px-4 py-2 font-bold transition ${
        isDarkTheme
          ? "bg-red-500 text-white hover:bg-red-600"
          : "border border-red-100 bg-red-50 text-red-700 hover:bg-red-100"
      }`}
    >
      Close
    </button>
  </div>

  <div className="flex-1 overflow-y-auto p-8 pt-4">
          

      
  <div className="grid gap-4 md:grid-cols-2">
        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Ordered</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>
            {formatDateTime(selectedBooking.created_at)}
          </p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Pickup Date & Time</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>
            {formatDate(selectedBooking.date)} at {selectedBooking.time}
          </p>
          {isASAPBooking(selectedBooking) && (
            <p className="mt-2 w-fit rounded-full bg-yellow-400 px-3 py-1 text-xs font-black uppercase tracking-wide text-black">
              ASAP pickup / Taxi for now
            </p>
          )}
        </div>

        <div className={`rounded-2xl border p-4 md:col-span-2 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Pickup</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>{selectedBooking.pickup}</p>
        </div>

        <div className={`rounded-2xl border p-4 md:col-span-2 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Destination</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>{selectedBooking.destination}</p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Phone</p>
          <input
  value={editingBooking?.phone || ""}
  onChange={(e) =>
    setEditingBooking({
      ...editingBooking,
      phone: e.target.value,
    })
  }
  className={`mt-2 w-full rounded-xl border px-3 py-2 outline-none focus:border-yellow-400 ${theme.input}`}
/>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Flight Number</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>
            {selectedBooking.flight_number || "Not provided"}
          </p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Passengers</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>{selectedBooking.passengers}</p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Luggage</p>
          <p className={`mt-1 font-bold ${theme.strongText}`}>{selectedBooking.luggage}</p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Estimated Price</p>
          <p className={`mt-1 font-bold ${isDarkTheme ? "text-yellow-400" : "text-yellow-700"}`}>
            €
            {calculateEstimatedPrice(
              selectedBooking.pickup,
              selectedBooking.destination,
              selectedBooking.luggage
            )}
          </p>
        </div>

        <div className={`rounded-2xl border p-4 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Final Price</p>
          <p className={`mt-1 font-bold ${isDarkTheme ? "text-yellow-400" : "text-yellow-700"}`}>
           <input
  type="number"
  value={editingBooking?.final_price || ""}
  onChange={(e) =>
    setEditingBooking({
      ...editingBooking,
      final_price: e.target.value,
    })
  }
  className={`mt-2 w-full rounded-xl border px-3 py-2 outline-none focus:border-yellow-400 ${theme.input}`}
/>
          </p>
        </div>

        <div className={`rounded-2xl border p-4 md:col-span-2 ${theme.softCard}`}>
          <p className={`text-sm ${theme.faintText}`}>Status</p>
          <select
  value={editingBooking?.status || ""}
  onChange={(e) =>
    setEditingBooking({
      ...editingBooking,
      status: e.target.value,
    })
  }
  className={`mt-2 w-full rounded-xl border px-3 py-2 outline-none focus:border-yellow-400 ${theme.input}`}
>
  <option value="new">New</option>
  <option value="confirmed">Confirmed</option>
  <option value="completed">Completed</option>
  <option value="cancelled">Cancelled</option>
</select>
        </div>
        <div className="md:col-span-2">
  <button
    onClick={saveBookingChanges}
    className="w-full rounded-xl bg-yellow-400 py-3 font-bold text-black hover:bg-yellow-300"
  >
    Save Changes
  </button>
</div>
      </div>
    </div>
  </div>
  </div>
  
)}
        </div>
      </main>
    </div>
     
  );
}

export default AdminPage;
